/**
 * L'identité de ce navigateur dans une séance.
 *
 * # Pourquoi elle doit être stable, et pourquoi pas trop
 *
 * LiveKit impose l'unicité : une seconde connexion sous la même identité
 * **expulse** la première. C'est ce qui règle les fantômes — un rechargement de
 * page, et le rechargement à chaud en produit beaucoup, ouvrait jusqu'ici une
 * connexion de plus sous une identité neuve, pendant que l'ancienne restait
 * dans la salle jusqu'à ce que LiveKit la remarque. Trois « Invité » pour deux
 * appareils.
 *
 * Mais elle ne doit pas être stable *par personne* : ouvrir un second onglet
 * fermerait le premier, et c'est exactement le geste qu'on fait pour éprouver
 * une séance à deux.
 *
 * D'où `sessionStorage` et non `localStorage` : il survit à un rechargement —
 * donc le fantôme est expulsé — mais chaque onglet a le sien, donc deux onglets
 * cohabitent. La portée de ce stockage décrit exactement le comportement voulu.
 *
 * # Le format
 *
 * Seize caractères hexadécimaux, comme ce que l'API tire elle-même. Il est
 * revalidé côté serveur : ce qui vient du navigateur n'est jamais cru sur
 * parole, et une valeur mal formée y sera remplacée plutôt que refusée.
 */

const STORAGE_KEY = "virtuosa.live.identity";
const PATTERN = /^p_[0-9a-f]{16}$/;

/**
 * L'identité de cet onglet, créée au premier appel.
 *
 * Rend `null` quand le stockage est refusé — navigation privée verrouillée,
 * cookies bloqués. L'API tire alors une identité au sort comme avant : on perd
 * l'expulsion du fantôme, pas la séance.
 */
export function tabIdentity(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);

    if (stored !== null && PATTERN.test(stored)) {
      return stored;
    }

    const created = `p_${randomHex(16)}`;
    window.sessionStorage.setItem(STORAGE_KEY, created);

    return created;
  } catch {
    return null;
  }
}

function randomHex(length: number): string {
  const bytes = new Uint8Array(length / 2);
  crypto.getRandomValues(bytes);

  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}
